import { asPartialDef } from "../coerce";
import { matchesAny, type NormalizeOpts } from "../normalize";
import type { BlockPlugin, GradeResult, ValidationResult } from "../types";

export interface FlashcardDef {
  front: string;
  back: string;
  /** Respuestas escritas aceptadas además de `back` (modo recall escrito). */
  accept?: string[];
  normalize?: NormalizeOpts;
}

/**
 * Dos modos:
 *  - Autoevaluado: `{ known: boolean }` (el alumno declara si lo sabía).
 *  - Recall escrito: `{ text }` corregido contra `back`+`accept`.
 */
export type FlashcardResponse = { known: boolean } | { text: string };

export const flashcardBlock: BlockPlugin<FlashcardDef, FlashcardResponse> = {
  type: "flashcard",
  interaction: "reveal",
  // Autoevaluado no es nota fiable; el antifraude lo pondera aparte.
  autoGradable: true,

  validate(def: unknown): ValidationResult<FlashcardDef> {
    const d = asPartialDef<FlashcardDef>(def);
    const errors: string[] = [];
    if (!d) {
      return { ok: false, errors: ["flashcard: definición no es un objeto"] };
    }
    if (typeof d.front !== "string" || d.front.trim() === "") {
      errors.push("flashcard: `front` requerido");
    }
    if (typeof d.back !== "string" || d.back.trim() === "") {
      errors.push("flashcard: `back` requerido");
    }
    if (errors.length > 0) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      def: {
        front: d.front ?? "",
        back: d.back ?? "",
        accept: d.accept,
        normalize: d.normalize,
      },
    };
  },

  grade(def: FlashcardDef, response: FlashcardResponse | undefined): GradeResult {
    const revealed = def.back;
    if (!response) {
      return { correct: false, score: 0, maxScore: 1, revealed };
    }
    if ("known" in response) {
      const ok = response.known;
      return { correct: ok, score: ok ? 1 : 0, maxScore: 1, revealed };
    }
    const accepted = [def.back, ...(def.accept ?? [])];
    const ok = matchesAny(response.text, accepted, def.normalize);
    return { correct: ok, score: ok ? 1 : 0, maxScore: 1, revealed };
  },
};
